import React, { useEffect, useState } from "react";
import MainLayout from "../../Layout/mainLayout";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { editCollection, viewOneCollection } from "../../Redux/Actions/collectionsResaleActions";
import AddImage from "../../Global Functions/addImage";
import toast from "react-hot-toast";
import "./editCollections.css";

const UploadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const UploadField = ({ label, fieldKey, value, onUpload, onRemove, loading, required }) => (
  <div className="ec-field">
    <label className="ec-label">
      {label}
      {required ? <span className="ec-req"> *</span> : <span className="ec-optional"> (Optional)</span>}
    </label>
    {value ? (
      <div className="ec-image-preview">
        <img src={value} alt={label} className="ec-preview-img" />
        <div className="ec-preview-info">
          <span className="ec-preview-label">Uploaded</span>
          <button type="button" className="ec-remove-btn" onClick={() => onRemove(fieldKey)} disabled={loading}>
            <TrashIcon />
            Remove
          </button>
        </div>
      </div>
    ) : (
      <label className="ec-upload-zone" htmlFor={`ec-upload-${fieldKey}`}>
        <input
          id={`ec-upload-${fieldKey}`}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => onUpload(e, fieldKey)}
          disabled={loading}
        />
        <div className="ec-upload-icon"><UploadIcon /></div>
        <span className="ec-upload-text">Click to upload</span>
        <span className="ec-upload-hint">PNG, JPG, SVG up to 2MB</span>
      </label>
    )}
  </div>
);

const EditCollections = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState(0);

  const [collectionState, setCollectionState] = useState({
    id: id,
    name: "",
    image: "",
    icon: "",
    three_d_icon: "",
    banner_image: "",
    banner_title: "",
    type: [],
  });

  useEffect(() => {
    dispatch(viewOneCollection(id));
  }, [id]);

  const { collectionResaleDetails } = useSelector((state) => state.collectionsResale || {});

  useEffect(() => {
    if (collectionResaleDetails?.data) {
      setCollectionState((prev) => ({
        ...prev,
        ...collectionResaleDetails.data,
        id: id,
        type: collectionResaleDetails.data.type || [],
      }));
    }
  }, [collectionResaleDetails, id]);

  const handleFileChange = async (event, type) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;
    const responseUrl = await AddImage(uploadedFile);
    if (responseUrl) {
      setCollectionState((prev) => ({ ...prev, [type]: responseUrl }));
    }
  };

  const handleRemove = (fieldKey) => {
    setCollectionState((prev) => ({ ...prev, [fieldKey]: "" }));
  };

  const handleNextSection = () => {
    if (activeSection === 0 && !collectionState.name.trim()) {
      toast.error("Collection name is required");
      return;
    }
    if (activeSection === 0 && collectionState.type.length === 0) {
      toast.error("Please select at least one type");
      return;
    }
    setActiveSection((s) => Math.min(sections.length - 1, s + 1));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!collectionState.name.trim()) return toast.error("Collection name is required");
    if (collectionState.type.length === 0) return toast.error("Please select at least one type");

    setLoading(true);
    try {
      const filteredData = Object.fromEntries(
        Object.entries(collectionState).filter(
          ([_, value]) => value !== "" && value !== null && value !== undefined
        )
      );
      const response = await dispatch(editCollection(filteredData));
      if (response?.statusCode === 200) {
        toast.success("Collection updated successfully!");
        navigate("/collections_resale");
      } else {
        toast.error(response?.message || "Failed to update collection");
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const sections = ["Basic Info", "Media"];

  return (
    <MainLayout>
      <div className="ec-wrapper">
        <div className="ec-header">
          <div className="ec-header-left">
            <div className="ec-header-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </div>
            <div>
              <h1 className="ec-title">Edit Collection</h1>
              <p className="ec-subtitle">Update the details of this collection</p>
            </div>
          </div>
          <Link to="/collections_resale" className="ec-view-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Go Back
          </Link>
        </div>

        <div className="ec-nav">
          {sections.map((sec, i) => (
            <button
              key={i}
              className={`ec-nav-pill ${activeSection === i ? "ec-nav-pill--active" : ""}`}
              onClick={() => setActiveSection(i)}
              type="button"
              disabled={loading}
            >
              <span className="ec-nav-num">{String(i + 1).padStart(2, "0")}</span>
              {sec}
            </button>
          ))}
        </div>

        <div className="ec-form">
          <div className={`ec-section ${activeSection === 0 ? "ec-section--visible" : ""}`}>
            <div className="ec-section-header">
              <span className="ec-section-badge">01</span>
              <h2 className="ec-section-title">Basic Information</h2>
            </div>
            <div className="ec-grid ec-grid-2">
              <div className="ec-field">
                <label className="ec-label">Collection Name <span className="ec-req">*</span></label>
                <input
                  className="ec-input"
                  type="text"
                  value={collectionState.name}
                  onChange={(e) => setCollectionState((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter Name"
                  disabled={loading}
                />
              </div>

              <div className="ec-field">
                <label className="ec-label">Banner Title <span className="ec-optional">(Optional)</span></label>
                <input
                  className="ec-input"
                  type="text"
                  value={collectionState.banner_title}
                  onChange={(e) => setCollectionState((prev) => ({ ...prev, banner_title: e.target.value }))}
                  placeholder="Enter Title"
                  disabled={loading}
                />
              </div>

              <div className="ec-field ec-field--full">
                <label className="ec-label">Type <span className="ec-req">*</span></label>
                <div className="ec-multi-select">
                  {["resale", "business"].map((option) => (
                    <label key={option} className="ec-checkbox-label">
                      <input
                        type="checkbox"
                        value={option}
                        checked={collectionState.type.includes(option)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setCollectionState((prev) => ({
                            ...prev,
                            type: checked
                              ? [...prev.type, option]
                              : prev.type.filter((t) => t !== option),
                          }));
                        }}
                        disabled={loading}
                      />
                      <span className="ec-checkbox-text">
                        {option.charAt(0).toUpperCase() + option.slice(1)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className={`ec-section ${activeSection === 1 ? "ec-section--visible" : ""}`}>
            <div className="ec-section-header">
              <span className="ec-section-badge">02</span>
              <h2 className="ec-section-title">Media</h2>
            </div>
            <div className="ec-grid ec-grid-2">
              <UploadField
                label="Collection Image"
                fieldKey="image"
                value={collectionState.image}
                onUpload={handleFileChange}
                onRemove={handleRemove}
                loading={loading}
              />
              <UploadField
                label="Collection Icon"
                fieldKey="icon"
                value={collectionState.icon}
                onUpload={handleFileChange}
                onRemove={handleRemove}
                loading={loading}
              />
              <UploadField
                label="Banner Image"
                fieldKey="banner_image"
                value={collectionState.banner_image}
                onUpload={handleFileChange}
                onRemove={handleRemove}
                loading={loading}
              />
              <UploadField
                label="3D Icon"
                fieldKey="three_d_icon"
                value={collectionState.three_d_icon}
                onUpload={handleFileChange}
                onRemove={handleRemove}
                loading={loading}
              />
            </div>
          </div>

          <div className="ec-footer">
            <div className="ec-footer-nav">
              <button
                type="button"
                className="ec-btn ec-btn--ghost"
                onClick={() => setActiveSection((s) => Math.max(0, s - 1))}
                disabled={activeSection === 0 || loading}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Back
              </button>

              {activeSection < sections.length - 1 ? (
                <button type="button" className="ec-btn ec-btn--primary" onClick={handleNextSection} disabled={loading}>
                  Next
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              ) : (
                <button type="button" onClick={handleSubmit} className="ec-btn ec-btn--submit" disabled={loading}>
                  {loading ? (
                    "Saving..."
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      Update Collection
                    </>
                  )}
                </button>
              )}
            </div>
            <Link to="/collections_resale" className="ec-btn ec-btn--cancel">
              Cancel
            </Link>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default EditCollections;